import { useSelector } from 'react-redux';
import { get } from 'lodash';
import { handleAccountStatus, selectAccounts, updateNetwork } from 'redux/slicers/accounts';
import { useAppDispatch, useAppSelector } from 'redux/store';
import { AccountFormData } from '../types';
import { handleValidation, validateRtiSettings, transformFormDataForCreate, transformFormDataForUpdate } from '../utils';
import { handleApiError, handleGenericError } from '../utils/errorHandler';
import { createAccount, updateAccount, upsertLicense, updateIntegrationsLicense, updateRtiSettings, getLicense, getNextAvailableDomain, updateDeduceConfiguration, getAssociatedNetworks } from 'redux/slicers/accounts';
import Toast, { TOAST_TYPES } from 'services/notifications';
import { IFormattedAccount } from 'models/Account';
import { IIdAndNamePair } from 'models/Common';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { DATATYPE, API_ERRORS } from '../constant';
import { Products, NEW_PRODUCT_NAMES, REBRANDED_PRODUCT_NAMES } from 'services/api/consts';
import { useSplit } from 'hooks';
import { SPLIT_CONSTANTS } from 'services/split.io';

interface UseAccountOperationsProps {
    currentAccount: IFormattedAccount | null;
    createNewAccount: boolean;
    isServeDomainTainted: boolean;
}

/**
 * Prepares account creation data and returns information needed for the dispatch calls
 * @param formData - The form data to create account with
 * @param productList - List of available products for proper labeling
 * @returns Object containing account data and additional configuration
 */
const handleCreate = (formData: AccountFormData, productList: IIdAndNamePair[] = []) => {
    // Transform form data to API format with proper product labels
    const accountData = transformFormDataForCreate(formData, productList);

    // Prepare RTI settings for CHEQ Defend if selected
    const hasDefendProduct = formData.activeLicenses.includes(Products.OSS);
    const rtiSettings =
        hasDefendProduct && formData.defendDataType.length > 0
            ? {
                  isApiIntegration: formData.defendDataType.includes(DATATYPE.SERVER),
                  isClientSideIntegration: formData.defendDataType.includes(DATATYPE.CLIENT),
                  status: true,
              }
            : null;

    return {
        accountData,
        rtiSettings,
        hasDefendProduct,
        licenseData: {
            products: formData.activeLicenses.map((id) => ({ id })),
        },
        integrationLicenseData: {
            products: formData.activeLicenses.map((id) => ({ value: id })),
        },
    };
};

/**
 * Prepares account update data and returns information needed for the dispatch calls
 * @param formData - The form data to update account with
 * @param currentAccount - The current account data for comparison
 * @returns Object containing update data and additional configuration
 */
const handleUpdate = (formData: AccountFormData, currentAccount: IFormattedAccount, productList: IIdAndNamePair[]) => {
    const updateData = transformFormDataForUpdate(formData, currentAccount, productList);

    // Prepare RTI settings for CHEQ Defend if selected
    const hasDefendProduct = formData.activeLicenses.includes(Products.OSS);
    const networkId = currentAccount.networkId || currentAccount.networks?.[0]?.id;

    const rtiSettings =
        hasDefendProduct && formData.defendDataType.length > 0 && networkId
            ? {
                  networkId,
                  isApiIntegration: formData.defendDataType.includes(DATATYPE.SERVER),
                  isClientSideIntegration: formData.defendDataType.includes(DATATYPE.CLIENT),
                  status: true,
              }
            : null;

    return {
        updateData,
        rtiSettings,
        hasDefendProduct,
        networkId,
        shouldUpdateLicenses: updateData.updateProducts && updateData.products,
        licenseData: updateData.updateProducts
            ? {
                  network_id: networkId,
                  products: updateData.products,
                  accountId: currentAccount.id,
              }
            : null,
        integrationLicenseData: updateData.updateProducts
            ? {
                  networkId,
                  products: updateData.products,
              }
            : null,
    };
};

/**
 * Handles RTI settings update with proper error handling
 */
const handleRtiSettingsUpdate = async (dispatch: ReturnType<typeof useAppDispatch>, rtiSettings: unknown, networkId: number, operationType: 'create' | 'update'): Promise<void> => {
    if (!rtiSettings) return;

    try {
        if (typeof rtiSettings !== 'object' || rtiSettings === null) {
            throw new Error(API_ERRORS.INVALID_RTI_SETTINGS);
        }

        const settingsPayload = { networkId, ...(rtiSettings as Record<string, unknown>) };
        if (!validateRtiSettings(settingsPayload)) {
            console.error('Invalid RTI settings provided:', settingsPayload);
            throw new Error(API_ERRORS.INVALID_RTI_SETTINGS);
        }
        const result = await dispatch(updateRtiSettings(settingsPayload));

        if (!updateRtiSettings.fulfilled.match(result)) {
            handleApiError(result, API_ERRORS.UNKNOWN_ERROR);
        }
    } catch (error) {
        console.error(`Error ${operationType}ing RTI settings:`, error);
        handleGenericError(error, `Failed to ${operationType} RTI settings`);
    }
};

export const useAccountOperations = ({ currentAccount, createNewAccount, isServeDomainTainted }: UseAccountOperationsProps) => {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { products = [] } = useSelector(selectAccounts);
    const salesforceAccounts = useAppSelector((state) => state.accounts.salesforceAccounts);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isUpdatedServingDomain, setIsUpdatedServingDomain] = useState(false);
    const { isOn: isAgentIntentRebrand } = useSplit(SPLIT_CONSTANTS.PD_AGENT_INTENT_REBRAND);
    const ossProductName = isAgentIntentRebrand ? REBRANDED_PRODUCT_NAMES[Products.OSS] : NEW_PRODUCT_NAMES[Products.OSS];

    const formatFormDataForSubmission = (data: AccountFormData) => ({
        ...data,
        servingDomain: createNewAccount || isServeDomainTainted ? data.servingDomain : '',
    });

    const handleAccountCreation = async (formattedData: AccountFormData) => {
        const createData = handleCreate(formattedData, products);

        const accountCreated = await dispatch(createAccount(createData.accountData));

        if (!createAccount.fulfilled.match(accountCreated)) {
            handleApiError(accountCreated, API_ERRORS.ACCOUNT_CREATE_FAILED);
        }

        const { networkId } = accountCreated.payload;

        // Only dispatch updateIntegrationsLicense if needed
        if (formattedData.activeLicenses.length > 0) {
            const integrationResult = await dispatch(updateIntegrationsLicense({ networkId, ...createData.integrationLicenseData }));
            if (!updateIntegrationsLicense.fulfilled.match(integrationResult)) {
                handleApiError(integrationResult, API_ERRORS.INTEGRATION_LICENSE_UPDATE_FAILED);
            }
        }

        // Handle RTI settings for CHEQ Defend if applicable
        if (createData.rtiSettings) {
            await handleRtiSettingsUpdate(dispatch, createData.rtiSettings, networkId, 'create');
        }

        // Get the created license details
        const licenseDetailsResult = await dispatch(getLicense({ id: networkId }));
        if (!getLicense.fulfilled.match(licenseDetailsResult)) {
            handleApiError(licenseDetailsResult, API_ERRORS.LICENSE_DETAILS_RETRIEVAL_FAILED);
        }
    };

    const handleAccountUpdate = async (formattedData: AccountFormData) => {
        if (!currentAccount) {
            throw new Error(API_ERRORS.CURRENT_ACCOUNT_REQUIRED);
        }

        const updateData = handleUpdate(formattedData, currentAccount, products);

        const accountUpdateResult = await dispatch(updateAccount(updateData.updateData));
        if (!updateAccount.fulfilled.match(accountUpdateResult)) {
            handleApiError(accountUpdateResult, API_ERRORS.ACCOUNT_UPDATE_FAILED);
        }
        // EditAccount syncs the account's networks server-side; refresh them so the UI reflects it.
        dispatch(getAssociatedNetworks({ accountId: currentAccount.id, force: true }));

        // Update licenses if products changed
        if (updateData.shouldUpdateLicenses && updateData.licenseData) {
            const licenseResult = await dispatch(upsertLicense(updateData.licenseData));
            if (!upsertLicense.fulfilled.match(licenseResult)) {
                handleApiError(licenseResult, API_ERRORS.LICENSE_UPDATE_FAILED);
            }

            if (updateData.integrationLicenseData) {
                const integrationResult = await dispatch(updateIntegrationsLicense(updateData.integrationLicenseData));
                if (!updateIntegrationsLicense.fulfilled.match(integrationResult)) {
                    handleApiError(integrationResult, API_ERRORS.INTEGRATION_LICENSE_UPDATE_FAILED);
                }
            }
        }

        // Handle RTI settings if applicable
        if (updateData.rtiSettings) {
            await handleRtiSettingsUpdate(dispatch, updateData.rtiSettings, currentAccount.networkId, 'update');
        }

        // If account activation status changed
        if (currentAccount.active !== formattedData.cheqAccountStatus && updateData.networkId) {
            const { orgId, id: accountId } = currentAccount;
            const statusResult = await dispatch(handleAccountStatus({ orgId, accountId, networkId: updateData.networkId, active: Boolean(formattedData.cheqAccountStatus) }));
            if (!handleAccountStatus.fulfilled.match(statusResult)) {
                handleApiError(statusResult, API_ERRORS.ACCOUNT_STATUS_UPDATE_FAILED);
            }
        }

        // If serving domain was changed
        const { networkId, id, networks } = currentAccount;
        const networkDiff = { id: networkId, account_id: id, whiteLabelDomain: get(networks, '[0].whiteLabelDomain', undefined) };
        if (networkDiff && isUpdatedServingDomain) {
            // @ts-ignore
            dispatch(updateNetwork(networkDiff));
        }

        // Handle Deduce configuration
        const deduceNetworkId = updateData.networkId || networks?.[0]?.id;
        if (deduceNetworkId) {
            const deducePayload =
                formattedData.deduceEnabled && formattedData.deduceSite && formattedData.deduceApiKey
                    ? { id: deduceNetworkId, site: formattedData.deduceSite, apiKey: formattedData.deduceApiKey }
                    : { id: deduceNetworkId };
            const deduceResult = await dispatch(updateDeduceConfiguration({ accountId: currentAccount.id, payload: deducePayload }));
            if (!updateDeduceConfiguration.fulfilled.match(deduceResult)) {
                handleApiError(deduceResult, API_ERRORS.DEDUCE_CONFIGURATION_UPDATE_FAILED);
            }
        }
    };

    const handleSuccess = () => {
        if (createNewAccount) {
            Toast({ type: TOAST_TYPES.SUCCESS, message: 'Account created successfully' });
            history.push('/accounts');
        } else {
            Toast({ type: TOAST_TYPES.SUCCESS, message: 'Account updated successfully' });
        }
    };

    const handleSubmit = async (formData: AccountFormData) => {
        const formattedFormData = formatFormDataForSubmission(formData);
        const validationErrors = handleValidation(formattedFormData, createNewAccount, ossProductName, salesforceAccounts);

        if (validationErrors.length > 0) {
            Toast({ type: TOAST_TYPES.ERROR, message: validationErrors[0] });
            return;
        }
        setIsSubmitting(true);
        try {
            createNewAccount ? await handleAccountCreation(formattedFormData) : await handleAccountUpdate(formattedFormData);
            handleSuccess();
        } catch (error) {
            console.error('Error during account operation:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleChangeServingDomain = () => {
        if (!currentAccount?.networks?.[0]) return;

        const accountId = currentAccount.id;
        const networkId = currentAccount.networks[0].id;

        dispatch(getNextAvailableDomain({ accountId, networkId })).then((result) => {
            if (getNextAvailableDomain.fulfilled.match(result)) setIsUpdatedServingDomain(true);
        });
    };

    return {
        handleSubmit,
        handleChangeServingDomain,
        isSubmitting,
    };
};
